import { FilesetResolver, FaceLandmarker } from "@mediapipe/tasks-vision";
import detectFaceAndEyesV2 from "./detectFaceAndEyesV2";
import _ from "lodash";
import { getImageDimention, sleep, asyncGetState, resizeImage, cropImages } from "../../utils/common";

let faceLandmarker;

export async function initFaceMesh() {
  const vision = await FilesetResolver.forVisionTasks(
    "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.11/wasm"
  );
  faceLandmarker = await FaceLandmarker.createFromOptions(vision, {
    baseOptions: {
      modelAssetPath: `https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task`,
      delegate: "GPU",
    },
    runningMode: "VIDEO",
    numFaces: 1,
  });
  return faceLandmarker;
}

export async function detectFacesFromVideo(
  _faceLandmarker,
  videoNode,
  canvas,
  frameWidth,
  frameHeight,
  lightingScore,
  previousLightingScore,
  finalImageWidth,
  finalImageHeight,
  isLandscapeMode
  // counter
) {
  let _lightingScore = lightingScore;
  let _previousLightingScore = previousLightingScore;
  let predictions;
  let lastVideoTime = -1;
  let nowInMs = Date.now();
  if (lastVideoTime !== videoNode?.currentTime) {
    lastVideoTime = videoNode?.currentTime;
    predictions = _faceLandmarker.detectForVideo(videoNode, nowInMs);
  }
  const detectResult = await detectFaceAndEyesV2(
    videoNode,
    canvas,
    frameWidth,
    frameHeight,
    predictions,
    _lightingScore,
    _previousLightingScore,
    finalImageWidth,
    finalImageHeight,
    isLandscapeMode
    // counter
  );

  return detectResult;
}

// export function closeFaceLandmarker(_faceLandmarker) {
//   if (_faceLandmarker) _faceLandmarker.close();
// }

export const playAudioInit = (url) => {
  return new Promise((resolve) => {
    const audio = new Audio(url);
    if (audio) {
      audio.onended = function () {
        resolve(audio);
      };
      resolve(audio);
    }
  });
};

export const playAudio = (audioSrc) => {
  const audioInstance = window.audioInstance;
  if(audioInstance) {
    return new Promise((resolve) => {
      audioInstance.src = audioSrc || "";
      audioInstance.play();
      audioInstance.onended = function () {
        resolve(audioInstance);
      };
    });
  }
};

export const resizeAndCropImages = async ({
  setThumbnailUrls,
  setthumbnailUrlCoordinates,
  setIsImageValidating,
  MAX_RESOLUTION_WIDTH,
  setEyeImages,
  setSmallImages,
  setSmallImageUrls
}) => {
  // Setting Images. Cropping and Resizing.
  setIsImageValidating(true);
  const _thumbnailUrls = await asyncGetState(setThumbnailUrls);
  const _thumbnailUrlCoordinates = await asyncGetState(setthumbnailUrlCoordinates);
  for await (const [imgSourceIndex, imgSource] of _thumbnailUrls.entries()) {
    // Crop Eye Image URI.
    const eyeImageUrl = await cropImages(imgSource, _thumbnailUrlCoordinates[imgSourceIndex]);
    // Convert Cropped Eye Image to Blob
    const eyeImage = await dataURLtoBlob(eyeImageUrl);
    await sleep(200); // short delay for reducing memory load.
    setEyeImages((oldEyeImages) => [...oldEyeImages, eyeImage]);

    // Note: Canvas height should not exceed the limit or IOS runs into memory issue.
    // Canvas area should not cross the maximum limit (width * height > 16777216).
    // https://github.com/wojtekmaj/react-pdf/issues/1149 This one is just for relevance.

    let drawWidth, drawHeight;
    // Resize orignal Image URI
    const imageDimention = await getImageDimention(imgSource);
    if (imageDimention?.width < MAX_RESOLUTION_WIDTH) {
      drawWidth = imageDimention?.width;
      drawHeight = imageDimention?.height;
    } else {
      const aspectRatio = imageDimention?.height / imageDimention?.width;
      drawWidth = MAX_RESOLUTION_WIDTH;
      drawHeight = MAX_RESOLUTION_WIDTH * aspectRatio;
    }
    const {dataUrl, dataPNGUrl} = await resizeImage(imgSource, drawWidth, drawHeight);
    setSmallImageUrls((oldSmallImageUrls) => [...oldSmallImageUrls, dataPNGUrl]);
    await sleep(200); // short delay for reducing memory load.

    // Convert Resied Original Image URI to Blob
    const smallImage = await dataURLtoBlob(dataUrl);
    await sleep(200); // short delay for reducing memory load.
    setSmallImages((oldSmallImages) => [...oldSmallImages, smallImage]);
  }
  setIsImageValidating(false);
};

const dataURLtoBlob = async (dataUrl) => {
  let arr = dataUrl?.split(","),
    mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[1]),
    n = bstr.length,
    u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new Blob([u8arr], { type: "image/jpeg" });
};

const resolutions = [
  {
    width: 7680,
    height: 4320
  },
  {
    width: 3840,
    height: 2160
  },
  {
    width: 2560,
    height: 1440
  },
  {
    width: 1920,
    height: 1080
  }
];


const result = {};
let deviceId;

const onGetUserMediaHandle = async (stream) => {
  const stream_settings = stream.getVideoTracks()[0].getSettings();
  const _deviceId = stream_settings?.deviceId;
  const _cameraWidth = stream_settings.width;
  const _cameraHeight = stream_settings.height;
  
  deviceId = _deviceId
  result[_deviceId] = result[_deviceId]
  ? [...result[_deviceId], { width: _cameraWidth, height: _cameraHeight }]
  : [{ width: _cameraWidth, height: _cameraHeight }];
  stream.getVideoTracks()[0].stop();
  
}
const onGetuserMediaErrorHandle = (error, setErrorMsg, setIsFailed) => {
  switch (error.name) {
    case "NotReadableError":
      setErrorMsg(
        "It looks like another app is using the camera already. Please turn off the camera in another app and click on the Retry button."
      );
      setIsFailed(true);
    case "NotFoundError":
      setErrorMsg("We cannot find your camera. Please connect your camera and click on the Retry button");
      setIsFailed(true);
    case "NotAllowedError":
      setErrorMsg(
        "Please change the permission settings on your browser to allow access to camera and click on the Retry button."
      );
      setIsFailed(true);
    default:
      setErrorMsg("We cannot access your camera. Please check your camera and click on the Retry button.");
      setIsFailed(true);
  }
};

export async function getMaxResolutionOfCamera({
  setCameraResolution,
  setIsCameraResolutionValid,
  setCameraAspectRatio,
  setFrameHeight,
  setHasCameraAccess,
  isMobile,
  setErrorMsg,
  setIsFailed
}) {
  for await (const res of resolutions) {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        width: res.width,
        height: res.height,
        aspectRatio: 4 / 3,
        facingMode: "user"
      }
    }).catch( error => onGetuserMediaErrorHandle(error, setErrorMsg, setIsFailed))
    if(stream) {
      await onGetUserMediaHandle(stream);
    }
  }
  if(_.isEmpty(deviceId) && _.isEmpty(result)) return;
  const biggestResolution = result[deviceId].reduce( (firstRes, nextRes) => {
    if(firstRes?.width > nextRes?.width) {
    return firstRes
    }
    return nextRes
  })
  
  const _cameraWidth = biggestResolution?.width;
  const _cameraHeight = biggestResolution?.height;
  const aspectRatio = isMobile
  ? _cameraWidth > _cameraHeight
  ? _cameraWidth / _cameraHeight
  : _cameraHeight / _cameraWidth
  : _cameraWidth > _cameraHeight ? _cameraHeight / _cameraWidth : _cameraWidth / _cameraHeight;

  const adjustedCameraWidth = _cameraWidth;
  const adjustedCameraHeight = _cameraHeight;
  setCameraResolution({ width: adjustedCameraWidth, height: adjustedCameraHeight });

  if (_cameraWidth < 2560 || _cameraHeight < 1440) {
    setIsCameraResolutionValid(true);
    return
  }

  if (isMobile) {
    const _aspectRatio =
      _cameraWidth > _cameraHeight ? _cameraWidth / _cameraHeight : _cameraHeight / _cameraWidth;
    setCameraAspectRatio(_aspectRatio);
    setFrameHeight(window.innerWidth * _aspectRatio * 0.95);
  } else {
    // setFrameHeight(window.innerWidth * _aspectRatio * 0.95);
    setCameraAspectRatio(aspectRatio);
    // setFrameHeight(frameWidth*aspectRatio); 
    setFrameHeight(360); 
  }
  setHasCameraAccess(true);
  return biggestResolution
}

